import { Grid, InputAdornment, TextField } from '@mui/material';
import {
  BenefitDTOBenefitEnum,
  CreateBenefitActivationDTO,
  LunchBenefitOptionsDTOTaxationTypeEnum,
  UpdateBenefitActivationDTO,
} from 'probonio-shared-ui/api';
import { RadioControl } from 'probonio-shared-ui/component/form';
import { apis } from 'probonio-shared-ui/module/api';
import { useTenant } from 'probonio-shared-ui/module/me';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { SelectControl, TextFieldControl } from '../../../../component/form';
import { BenefitActivationDialogExtensionInterface } from '../../../../extension/interface/benefit-activation-dialog.extension.interface';
import { ActivationDialogExtensions } from '../../ActivationDialog';

interface LunchBenefitOptions {
  dailyLimit: number;
  maxNumberOfReceipts: number;
  taxationType: LunchBenefitOptionsDTOTaxationTypeEnum;
  salaryConversation: boolean;
}

const MEALS_NUMBERS = Array(15)
  .fill(0)
  .map((e: string, i: number) => i + 1);

const Fields: React.FC = () => {
  const { t } = useTranslation('benefitsModule');
  const { control, watch } = useFormContext();
  const { tenant } = useTenant();

  const limit = watch('benefitOptions.dailyLimit');
  const price = watch('benefitOptions.maxNumberOfReceipts');

  const taxationMaxDailyLimit = tenant?.taxConfig && (tenant.taxConfig.lunchNonCashValue + tenant.taxConfig.lunchSupplement) / 100;

  return (
    <Grid
      container
      spacing={2}
      sx={{
        marginBottom: 1,
      }}
    >
      <Grid size={4}>
        <SelectControl
          control={control}
          name="benefitOptions.maxNumberOfReceipts"
          label={t('LUNCH.fields.mealsPerMonth')}
          fullWidth
          margin="dense"
          rules={{ required: true }}
          options={MEALS_NUMBERS.map(n => ({ value: n, label: n }))}
        />
      </Grid>
      <Grid size={4}>
        <TextFieldControl
          slotProps={{ input: { endAdornment: <InputAdornment position="end">€</InputAdornment> }, htmlInput: { step: 0.01 } }}
          rules={{
            required: true,
            min: 0.01,
            max: taxationMaxDailyLimit && {
              value: taxationMaxDailyLimit,
              message: t('LUNCH.maxDailyLimitError', { value: taxationMaxDailyLimit * 100 }),
            },
          }}
          type="number"
          control={control}
          variant="outlined"
          name="benefitOptions.dailyLimit"
          label={t('LUNCH.fields.perMeal')}
          margin="dense"
          fullWidth
        />
      </Grid>
      <Grid size={4}>
        <TextField
          slotProps={{ input: { endAdornment: <InputAdornment position="end">€</InputAdornment> } }}
          value={(price * +limit).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
          margin="dense"
          disabled
          label={t('LUNCH.fields.maxAmount')}
        />
      </Grid>
      <Grid size={12}>
        <RadioControl
          control={control}
          name="benefitOptions.taxationType"
          options={[
            {
              label: t('LUNCH.fields.taxationType.FLAT_RATE_TAXATION'),
              value: LunchBenefitOptionsDTOTaxationTypeEnum.FlatRateTaxation,
            },
            {
              label: t('LUNCH.fields.taxationType.SIMPLE_TAXATION'),
              value: LunchBenefitOptionsDTOTaxationTypeEnum.SimpleTaxation,
            },
          ]}
        />
      </Grid>
      {/* currently deactivated due to regulatory issues
      <Grid item xs={12}>
        <CheckboxControl disabled control={control} name="benefitOptions.salaryConversation" label={t('LUNCH.fields.salaryConversation')} />
      </Grid> */}
    </Grid>
  );
};

const mapLunchOptions = (benefitOptions: LunchBenefitOptions) => ({
  maxNumberOfReceipts: benefitOptions.maxNumberOfReceipts,
  dailyLimit: Math.round(benefitOptions.dailyLimit * 100),
  taxationType: benefitOptions.taxationType,
  salaryConversation: benefitOptions.salaryConversation,
});

const createLunchActivation: ActivationDialogExtensions<LunchBenefitOptions>['onCreateActivation'] = async (
  baseValues,
  benefitOptions,
  saveActivation,
  context,
) => {
  const create: CreateBenefitActivationDTO = {
    ...baseValues,
    benefit: BenefitDTOBenefitEnum.Lunch,
    lunchOptions: mapLunchOptions(benefitOptions),
  };
  await saveActivation(create);

  if (context?.tenant?.lunchTaxationType !== benefitOptions.taxationType && context?.tenant) {
    await apis.tenants.updateTenant({ tenantId: context?.tenant?.id, updateTenantDTO: { lunchTaxationType: benefitOptions.taxationType } });
    await context.queryClient.invalidateQueries({ queryKey: ['tenants', context.tenant.id] });
  }
};

const updateLunchActivation: ActivationDialogExtensions<LunchBenefitOptions>['onUpdateActivation'] = async (
  baseValues,
  benefitOptions,
  saveActivation,
  context,
) => {
  const update: UpdateBenefitActivationDTO = {
    ...baseValues,
    lunchOptions: mapLunchOptions(benefitOptions),
  };
  await saveActivation(update);

  if (context?.tenant?.lunchTaxationType !== benefitOptions.taxationType && context?.tenant) {
    await apis.tenants.updateTenant({ tenantId: context.tenant.id, updateTenantDTO: { lunchTaxationType: benefitOptions.taxationType } });
    await context.queryClient.invalidateQueries({ queryKey: ['tenants', context.tenant.id] });
  }
};

const resetLunchBenefitOptions: ActivationDialogExtensions<LunchBenefitOptions>['onResetBenefitOptions'] = (
  selectedActivation,
  context,
) => {
  const taxationMaxDailyLimit =
    context?.tenant?.taxConfig && (context.tenant.taxConfig.lunchNonCashValue + context.tenant.taxConfig.lunchSupplement) / 100;

  return {
    dailyLimit: selectedActivation?.lunchOptions ? selectedActivation.lunchOptions.dailyLimit / 100 : taxationMaxDailyLimit || 0,
    maxNumberOfReceipts: selectedActivation?.lunchOptions ? selectedActivation.lunchOptions.maxNumberOfReceipts : 15,
    taxationType: selectedActivation?.lunchOptions
      ? selectedActivation.lunchOptions.taxationType
      : context?.tenant?.lunchTaxationType || LunchBenefitOptionsDTOTaxationTypeEnum.FlatRateTaxation,
    salaryConversation: selectedActivation?.lunchOptions?.salaryConversation || false,
  };
};

export class LunchBenefitActivationDialogExtension extends BenefitActivationDialogExtensionInterface<LunchBenefitOptions> {
  CreateFields?: React.ReactNode = (<Fields />);
  ChangeOptionsFields?: React.ReactNode = (<Fields />);

  onCreateActivation = createLunchActivation;
  onUpdateActivation = updateLunchActivation;
  onResetBenefitOptions? = resetLunchBenefitOptions;
}
